import { useEffect, useState } from "react";
import { FileImage, UserRound } from "lucide-react";
import { Loader } from "@/components/ui/Loader";
import { Modal } from "@/components/ui/Modal";
import api from "@/services/api";
import {
  fetchChamadoById,
  type ChamadoDto,
  type ChamadoStatus,
  type ChamadoTipo,
} from "@/services/chamados";
import { formatDateTime } from "@/utils/format";

interface Props {
  chamadoId: string | null;
  open: boolean;
  onClose: () => void;
}

const TIPO_LABELS: Record<ChamadoTipo, string> = {
  bug: "Bug",
  duvida: "Dúvida",
  acesso: "Acesso",
  sugestao: "Sugestão",
};

const STATUS_LABELS: Record<ChamadoStatus, string> = {
  esperando_analise: "Esperando Análise",
  em_analise: "Em Análise",
  corrigindo: "Corrigindo",
  esperando_validacao: "Esperando Validação",
  finalizada: "Finalizada",
};

function apiErrorMessage(error: unknown): string {
  if (error && typeof error === "object" && "response" in error) {
    const response = (error as { response?: { data?: { error?: unknown } } }).response;
    if (typeof response?.data?.error === "string") return response.data.error;
  }
  return error instanceof Error ? error.message : "Não foi possível carregar o chamado.";
}

export function ChamadoDetalheModal({ chamadoId, open, onClose }: Props) {
  const [chamado, setChamado] = useState<ChamadoDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [printUrl, setPrintUrl] = useState<string | null>(null);
  const [printError, setPrintError] = useState("");

  useEffect(() => {
    if (!open || !chamadoId) {
      setChamado(null);
      setError("");
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError("");
    setChamado(null);

    void (async () => {
      try {
        const detail = await fetchChamadoById(chamadoId);
        if (!cancelled) setChamado(detail);
      } catch (loadError) {
        if (!cancelled) setError(apiErrorMessage(loadError));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, chamadoId]);

  useEffect(() => {
    if (!open || !chamado?.printPath) {
      setPrintUrl(null);
      setPrintError("");
      return;
    }

    let objectUrl: string | null = null;
    let cancelled = false;

    void (async () => {
      setPrintError("");
      try {
        const { data } = await api.get<Blob>(
          `/api/chamados/${encodeURIComponent(chamado.id)}/print`,
          { responseType: "blob" },
        );
        if (cancelled) return;
        objectUrl = URL.createObjectURL(data);
        setPrintUrl(objectUrl);
      } catch {
        if (!cancelled) {
          setPrintUrl(null);
          setPrintError("Não foi possível carregar o print anexado.");
        }
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, chamado?.id, chamado?.printPath]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={chamado?.titulo ?? "Conteúdo do chamado"}
      size="lg"
      closeOnEscape
      closeOnBackdropClick
    >
      {loading && (
        <div className="flex justify-center py-12">
          <Loader label="Carregando conteúdo..." />
        </div>
      )}

      {!loading && error && (
        <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </p>
      )}

      {!loading && !error && chamado && (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
              {chamado.modulo || "Sem módulo"}
            </span>
            <span className="rounded-full bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700">
              {TIPO_LABELS[chamado.tipo] ?? chamado.tipo}
            </span>
            <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800">
              {STATUS_LABELS[chamado.status] ?? chamado.status}
            </span>
          </div>

          <div className="grid gap-2 text-sm text-slate-600 sm:grid-cols-2">
            <p className="flex items-center gap-1.5">
              <UserRound className="h-4 w-4 text-slate-400" />
              <span>
                Solicitante:{" "}
                <span className="font-medium text-slate-800">{chamado.autorNome}</span>
              </span>
            </p>
            <p>
              Criado em:{" "}
              <span className="font-medium text-slate-800">
                {formatDateTime(chamado.createdAt)}
              </span>
            </p>
            {chamado.contexto && (
              <p className="sm:col-span-2">
                Contexto:{" "}
                <span className="font-medium text-slate-800">{chamado.contexto}</span>
              </p>
            )}
          </div>

          <div>
            <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">
              Descrição do chamado
            </h3>
            <div className="whitespace-pre-wrap rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-relaxed text-slate-700">
              {chamado.descricao || "Nenhuma descrição informada."}
            </div>
          </div>

          {chamado.printPath && (
            <div>
              <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">
                Print anexado
              </h3>
              {printError && (
                <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {printError}
                </p>
              )}
              {printUrl ? (
                <a
                  href={printUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="block overflow-hidden rounded-xl border border-slate-200 bg-white"
                >
                  <img
                    src={printUrl}
                    alt={chamado.printOriginalName || "Print do chamado"}
                    className="max-h-80 w-full object-contain"
                  />
                </a>
              ) : (
                !printError && (
                  <div className="flex items-center gap-2 rounded-xl border border-dashed border-slate-200 px-4 py-8 text-sm text-slate-400">
                    <FileImage className="h-5 w-5" />
                    Carregando print...
                  </div>
                )
              )}
              {chamado.printOriginalName && (
                <p className="mt-1.5 text-xs text-slate-400">{chamado.printOriginalName}</p>
              )}
            </div>
          )}

          {(chamado.comentarios?.length ?? 0) > 0 && (
            <div>
              <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">
                Comentários
              </h3>
              <div className="space-y-2">
                {chamado.comentarios.map((comentario) => (
                  <div
                    key={comentario.id}
                    className="rounded-lg border border-slate-100 bg-white px-3 py-2 text-sm"
                  >
                    <p className="whitespace-pre-wrap text-slate-700">{comentario.texto}</p>
                    <p className="mt-1 text-xs text-slate-400">
                      {comentario.autorNome} · {formatDateTime(comentario.createdAt)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
